import { SkyButton } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { sqlPortOf } from "@shared/connectors/sqlSignInRefusal";
import {
  reachDetailLines,
  reachDetailsText,
  type ReachDetailLine,
} from "@shared/connectors/reachDetails";
import { formatInstant } from "@/lib/time/displayTime";
import { MASKED_VALUE } from "@shared/types/dataflows";

/**
 * #2485: what the "skyData could not check the connection" card adds for a SQL Server source it
 * did not reach: the server and port that were tried (read from the form, on screen only, never
 * stored or logged), the time and the answer's reference id, and a
 * Copy details button for the network team. A Viewer's server reads withheld (#1107), so its line
 * is left out.
 */

/** The server a form holds, or null when it is empty, masked or withheld. */
export function reachServerOf(values: Readonly<Record<string, unknown>>): string | null {
  const server = values.serverName;
  if (typeof server !== "string") return null;
  const text = server.trim();
  return text === "" || text === MASKED_VALUE ? null : text;
}

/** Copies in the click itself (a clipboard write needs the gesture), else selects nothing and says so. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the textarea copy.
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const done = document.execCommand("copy");
    area.remove();
    return done;
  } catch {
    return false;
  }
}

const COPY_STATE_TEXT = {
  idle: "",
  copied: "Copied.",
  failed: "The details could not be copied. Select the lines above and copy them instead.",
} as const;

export function ReachDetails(props: {
  values: Readonly<Record<string, unknown>>;
  reference: string | null;
  at: string;
  zone: string;
  testId: string;
}) {
  const { values, reference, at, zone, testId } = props;
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const lines: ReachDetailLine[] = reachDetailLines({
    server: reachServerOf(values),
    port: sqlPortOf(values),
    time: formatInstant(at, zone, "minutes", { named: true }),
    reference,
  });
  const onCopy = () => {
    // The write starts here, inside the click, before any await.
    void copyText(reachDetailsText(lines)).then((done) => setState(done ? "copied" : "failed"));
  };
  return (
    <div className="mt-2" data-testid={testId}>
      <dl className="m-0">
        {lines.map((line) => (
          <div key={line.label} className="flex gap-2">
            <dt className="font-semibold">{line.label}:</dt>
            <dd
              className="m-0 break-all"
              data-testid={`${testId}-${line.label.toLowerCase().replace(/\W+/g, "-")}`}
            >
              {line.value}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-2 flex items-center gap-2">
        <SkyButton size="small" appearance="secondary" onClick={onCopy} testId={`${testId}-copy`}>
          Copy details
        </SkyButton>
        <output className="text-sm" data-testid={`${testId}-copy-state`}>
          {COPY_STATE_TEXT[state]}
        </output>
      </div>
    </div>
  );
}
